/**
 * 会社(クライアント)マスタ(companies)。仕様書 §4.2。
 * docId は CL-001 形式(自動採番)。
 * 会社単価の変更は「改訂を追加」でのみ行い、price_history[] に必ず残す(NEXUS Gate と同方式)。
 * 登録・編集は audit_logs に残す。
 */
import {
  Timestamp,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { nextCompanyCode } from '@core/lib/numbering';
import { writeAuditLog } from '@core/lib/auditLog';
import { isValidDateStr, todayStr } from '@core/lib/date';
import { isValidAmount } from '@core/lib/money';
import { priceOn, type BusinessType, type Company, type CompanyStatus, type DeliveryPlan, type PriceRevision, type Yen } from '@core/lib/types';

function withId<T>(snap: QueryDocumentSnapshot): T {
  return { id: snap.id, ...(snap.data() as object) } as T;
}

export type CompanyInput = {
  name: string;
  unit_price: Yen;
  product_type: BusinessType;
  delivery_timing: string;
  bb_business_codes: string[];
  phone: string;
  status: CompanyStatus;
  note: string;
  /** 曜日別の納品件数の予定(2026-09-24。D-145) */
  delivery_plan?: DeliveryPlan[];
};

export type PriceRevisionInput = { effective_from: string; price: Yen; reason: string };

function validate(input: CompanyInput): void {
  if (!input.name.trim()) throw new Error('社名を入力してください');
  if (!isValidAmount(input.unit_price)) throw new Error('会社単価は0以上の整数(円)で入力してください');
}

function toRevisions(list: PriceRevisionInput[], userId: string): PriceRevision[] {
  return list.map((r) => {
    if (!isValidDateStr(r.effective_from)) throw new Error('改訂の適用開始日を選んでください');
    if (!isValidAmount(r.price)) throw new Error('改訂後の単価は0以上の整数(円)で入力してください');
    if (!r.reason.trim()) throw new Error('改訂の理由を入力してください');
    return { price: r.price, effective_from: r.effective_from, reason: r.reason.trim(), changed_by: userId, changed_at: Timestamp.now() };
  });
}

function sortHistory(history: PriceRevision[]): PriceRevision[] {
  return [...history].sort((a, b) => (a.effective_from < b.effective_from ? -1 : a.effective_from > b.effective_from ? 1 : 0));
}

/** 曜日別の納品件数: 日付が正しいものだけ・7つの数字にそろえる(適用開始日の古い順) */
function normalizePlans(list: DeliveryPlan[]): DeliveryPlan[] {
  return list
    .filter((p) => /^\d{4}-\d{2}-\d{2}$/.test(String(p?.from ?? '')))
    .map((p) => ({ from: p.from, counts: Array.from({ length: 7 }, (_, i) => Math.max(0, Math.round(Number(p.counts?.[i] ?? 0)) || 0)) }))
    .sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
}

function toDocument(input: CompanyInput) {
  return {
    name: input.name.trim(),
    product_type: input.product_type,
    delivery_timing: input.delivery_timing.trim(),
    bb_business_codes: input.bb_business_codes.map((c) => c.trim()).filter((c) => c !== ''),
    phone: input.phone.trim() || null,
    status: input.status,
    note: input.note.trim() || null,
    delivery_plan: normalizePlans(input.delivery_plan ?? []),
  };
}

export async function fetchCompanies(): Promise<Company[]> {
  const snap = await getDocs(query(collection(getDb(), 'companies'), orderBy('__name__')));
  return snap.docs.map((d) => withId<Company>(d));
}

export async function createCompany(
  input: CompanyInput,
  userId: string,
  options: { source?: 'manual' | 'import'; revisions?: PriceRevisionInput[] } = {},
): Promise<string> {
  validate(input);
  const id = await nextCompanyCode();
  const first: PriceRevision = {
    price: input.unit_price,
    effective_from: todayStr(),
    reason: options.source === 'import' ? 'CSVインポート(初期登録)' : '初期登録',
    changed_by: userId,
    changed_at: Timestamp.now(),
  };
  const history = sortHistory([first, ...toRevisions(options.revisions ?? [], userId)]);
  const now = serverTimestamp();
  await setDoc(doc(getDb(), 'companies', id), {
    ...toDocument(input),
    unit_price: priceOn({ unit_price: input.unit_price, price_history: history }, todayStr()),
    price_history: history,
    created_by: userId,
    created_at: now,
    updated_by: userId,
    updated_at: now,
  });
  await writeAuditLog({
    targetType: 'company',
    targetId: id,
    action: options.source === 'import' ? 'company-import' : 'company-create',
    by: userId,
    after: { name: input.name.trim(), price: input.unit_price },
  });
  return id;
}

/** 更新。会社単価は「改訂を追加」(revisions)でのみ変わる */
export async function updateCompany(params: {
  current: Company;
  input: CompanyInput;
  revisions: PriceRevisionInput[];
  changes: string[];
  userId: string;
}): Promise<void> {
  const { current, input, revisions, changes, userId } = params;
  validate({ ...input, unit_price: current.unit_price });
  const history = sortHistory([...(current.price_history ?? []), ...toRevisions(revisions, userId)]);
  const price = priceOn({ unit_price: current.unit_price, price_history: history }, todayStr());
  await updateDoc(doc(getDb(), 'companies', current.id), {
    ...toDocument(input),
    unit_price: price,
    price_history: history,
    updated_by: userId,
    updated_at: serverTimestamp(),
  });
  await writeAuditLog({
    targetType: 'company',
    targetId: current.id,
    action: revisions.length > 0 ? 'company-price-change' : 'company-update',
    by: userId,
    before: { price: current.unit_price },
    after: { price },
    changes,
  });
}

/** 削除(取込ミスの取り消し用)。通常はステータスを「停止」に変更する */
export async function deleteCompany(company: Company, userId: string): Promise<void> {
  await deleteDoc(doc(getDb(), 'companies', company.id));
  await writeAuditLog({
    targetType: 'company',
    targetId: company.id,
    action: 'company-delete',
    by: userId,
    before: { name: company.name },
  });
}

/**
 * 曜日別の納品件数の予定を **まとめて** 保存する(設定 → 納品件数の予定。2026-09-24 D-145)。
 * 同じ適用開始日の予定があれば置き換え、無ければ足す。会社ごとに1回だけ書く
 */
export async function saveDeliveryPlans(list: { id: string; plan: DeliveryPlan }[], userId: string): Promise<void> {
  const db = getDb();
  for (const { id, plan } of list) {
    if (!isValidDateStr(plan.from)) throw new Error('適用開始日を選んでください');
    const ref = doc(db, 'companies', id);
    const cur = (await getDoc(ref)).data() as Company | undefined;
    const rest = (cur?.delivery_plan ?? []).filter((p) => p.from !== plan.from);
    const next = normalizePlans([...rest, plan]);
    await setDoc(ref, { delivery_plan: next, updated_by: userId, updated_at: serverTimestamp() }, { merge: true });
    await writeAuditLog({
      targetType: 'company',
      targetId: id,
      action: 'company-update',
      by: userId,
      changes: [`納品件数の予定(${plan.from}〜): ${plan.counts.join(' / ')}`],
    });
  }
}
